import { useMemo } from "react";
import { Link } from "react-router";
import { Building2, User, Briefcase } from "lucide-react";
import { useVendors } from "../../lib/vendors/useVendors";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { formatAmountCents, formatIsoDate } from "../../lib/format";
import type { Vendor, VendorType } from "../../lib/vendors/types";
import { VendorLogo } from "../components/VendorLogo";
import { ScopePinnedBadge } from "../components/ScopePinnedBadge";
import { useAuth } from "../../lib/auth/useAuth";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { useEffectiveScopeForTab } from "../../lib/scope";

// Wave 9 Vendors list. Vendor rows are auto-created whenever an expense or
// invoice is submitted (see src/lib/expenses/*.ts and src/lib/invoices/*.ts).
// The Map of per-vendor stats is computed once from the already-loaded
// expense+invoice snapshots so we don't pay a second round trip.

interface VendorRow {
  vendor: Vendor;
  totalCents: number;
  txnCount: number;
  lastActivityIso: string | null;
}

function VendorTypeBadge({ type }: { type: VendorType }) {
  const iconCls = "w-3.5 h-3.5";
  switch (type) {
    case "customer":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 rounded-lg border border-green-100">
          <User className={iconCls} /> Customer
        </span>
      );
    case "employee":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg border border-amber-100">
          <Briefcase className={iconCls} /> Employee
        </span>
      );
    case "other":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-50 text-slate-600 rounded-lg border border-slate-200">
          <Building2 className={iconCls} /> Other
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg border border-blue-100">
          <Building2 className={iconCls} /> Vendor
        </span>
      );
  }
}

export function Vendors() {
  const { vendors } = useVendors();
  const { expenses } = useExpenses();
  const { invoices } = useInvoices();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { scope, pinned } = useEffectiveScopeForTab("vendors");
  const userId = user?.id ?? null;
  const canWrite = can("write:vendors");

  // Wave 22 — in "mine" scope a vendor is visible when at least one of the
  // current user's expenses OR invoices references it. We derive this from
  // the already-loaded snapshots rather than going back to the server to
  // keep the page a pure client view.
  //
  // Perf note: this is O(expenses + invoices + vendors). For very large orgs
  // the two per-user filter passes dominate, but they share the same scan
  // used for stats, so the effective constant factor is small. If rows ever
  // get into the tens of thousands, a server-side `mine` query would be the
  // next optimisation (see `src/lib/vendors/supabaseStore.ts`).
  const scopedVendorIds = useMemo<Set<string> | null>(() => {
    if (scope === "company") return null;
    if (!userId) return new Set<string>();
    const ids = new Set<string>();
    for (const e of expenses) {
      if (e.vendorId && e.submittedBy === userId) ids.add(e.vendorId);
    }
    for (const i of invoices) {
      if (i.vendorId && i.submittedBy === userId) ids.add(i.vendorId);
    }
    return ids;
  }, [scope, userId, expenses, invoices]);

  const rows = useMemo<VendorRow[]>(() => {
    // Accumulate stats in a Map keyed by vendor id so the two passes over
    // expenses/invoices stay O(n). In "mine" scope we tighten both stats
    // and the vendor list to rows the current user touched.
    const stats = new Map<
      string,
      { totalCents: number; txnCount: number; lastActivityIso: string | null }
    >();
    const bump = (vendorId: string | null, amountCents: number, iso: string) => {
      if (!vendorId) return;
      const cur = stats.get(vendorId) ?? {
        totalCents: 0,
        txnCount: 0,
        lastActivityIso: null as string | null,
      };
      cur.totalCents += amountCents;
      cur.txnCount += 1;
      if (!cur.lastActivityIso || iso > cur.lastActivityIso)
        cur.lastActivityIso = iso;
      stats.set(vendorId, cur);
    };
    for (const e of expenses) {
      if (scope === "mine" && e.submittedBy !== userId) continue;
      bump(e.vendorId, e.amountCents, e.occurredOn);
    }
    for (const i of invoices) {
      if (scope === "mine" && i.submittedBy !== userId) continue;
      bump(i.vendorId, i.amountCents, i.issuedOn);
    }

    const visibleVendors = scopedVendorIds == null
      ? vendors
      : vendors.filter((v) => scopedVendorIds.has(v.id));

    const list = visibleVendors.map((vendor) => {
      const s = stats.get(vendor.id) ?? {
        totalCents: 0,
        txnCount: 0,
        lastActivityIso: null,
      };
      return { vendor, ...s };
    });
    // Default sort: total spend descending, canonical name ascending on tie.
    list.sort((a, b) => {
      if (b.totalCents !== a.totalCents) return b.totalCents - a.totalCents;
      return a.vendor.canonicalName.localeCompare(b.vendor.canonicalName);
    });
    return list;
  }, [vendors, expenses, invoices, scope, userId, scopedVendorIds]);

  return (
    <div
      data-testid="vendors-page"
      data-scope={scope}
      data-can-write={canWrite ? "true" : "false"}
      className="flex flex-col gap-4 max-w-6xl mx-auto"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900">Vendors</h1>
            {pinned && <ScopePinnedBadge forcedScope={scope} />}
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            Auto-linked from every expense and invoice you submit.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {rows.length === 0 ? (
          <div
            data-testid="vendors-empty"
            className="p-10 text-center text-sm text-slate-500"
          >
            No vendors yet. Create an expense or invoice and the vendor will
            appear here.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5 text-right">Transactions</th>
                <th className="px-4 py-2.5 text-right">Total spend</th>
                <th className="px-4 py-2.5">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.vendor.id}
                  data-testid={`vendor-row-${row.vendor.id}`}
                  className="border-b last:border-b-0 border-slate-100 hover:bg-slate-50 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/vendors/${row.vendor.id}`}
                      data-testid={`vendor-row-link-${row.vendor.id}`}
                      className="inline-flex items-center gap-2 font-medium text-slate-900 hover:text-[#0070F2]"
                    >
                      <VendorLogo vendor={row.vendor} size={24} />
                      {row.vendor.canonicalName}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <VendorTypeBadge type={row.vendor.type} />
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular-nums text-slate-700"
                    data-testid={`vendor-row-txn-count-${row.vendor.id}`}
                  >
                    {row.txnCount}
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular-nums text-slate-900 font-medium"
                    data-testid={`vendor-row-total-${row.vendor.id}`}
                  >
                    {formatAmountCents(row.totalCents)}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {row.lastActivityIso
                      ? formatIsoDate(row.lastActivityIso)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
